import { useRef } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const POINTS = [
  {
    icon: 'ri-fingerprint-line',
    title: 'Custom-Designed Solutions',
    desc: 'No templates. Every project is designed from scratch to match your property\'s unique character and your personal vision.',
  },
  {
    icon: 'ri-gem-line',
    title: 'Premium Materials & Finishes',
    desc: 'We source only the finest stone, plants, and structural materials — chosen for beauty, durability, and long-term performance.',
  },
  {
    icon: 'ri-group-line',
    title: 'Expert Installation Team',
    desc: 'Our trained crews — not subcontractors — build your project to exact specifications with meticulous attention to detail.',
  },
  {
    icon: 'ri-message-3-line',
    title: 'Clear Communication Always',
    desc: 'You receive regular updates and a dedicated point of contact from consultation to final walkthrough.',
  },
  {
    icon: 'ri-time-line',
    title: 'Built for Longevity & Value',
    desc: 'Every decision prioritizes structural integrity and lasting beauty — investments that increase your home\'s market value.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Deep Local Expertise',
    desc: 'We understand New Mexico\'s climate and soils — designing landscapes that thrive beautifully in your environment.',
  },
];

export default function WhyChooseUs() {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.1 });

  return (
    <section className="bg-ivory-100 py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <div
          ref={ref}
          className={`grid md:grid-cols-2 gap-20 items-stretch transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left: Stacked images with overlap */}
          <div className="relative">
            {/* Primary image */}
            <div className="w-full h-[580px] rounded-lg overflow-hidden">
              <img
                src="https://brainardsgreenscapes.com/assets/images/about.jpg"
                alt="Brainard's Greenscapes team and project"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Floating inset image */}
            <div className="absolute -bottom-10 -right-8 w-52 h-52 rounded-lg overflow-hidden border-4 border-ivory-100">
              <img
                src="https://brainardsgreenscapes.com/assets/images/hardscapinghome.jpg"
                alt="Premium hardscape detail"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 left-8 bg-charcoal-900 text-white rounded-lg p-6 w-48">
              <div className="font-serif text-5xl font-bold text-white">19+</div>
              <div className="font-sans text-stone-400 text-xs mt-1.5 leading-tight tracking-wide uppercase">Years Elevating Outdoor Spaces in NM</div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col justify-center pt-4 pb-12">
            <p className="font-sans text-forest-500 text-xs tracking-[0.25em] uppercase mb-4">Why Brainard's</p>
            <h2 className="font-serif text-charcoal-900 font-semibold leading-tight mb-4" style={{ fontSize: 'clamp(32px, 3.5vw, 52px)' }}>
              Built for Homeowners<br />
              <em className="italic text-forest-500">Who Expect More</em>
            </h2>
            <div className="w-12 h-0.5 bg-stone-400 mb-6" />
            <p className="font-sans text-charcoal-700 text-base leading-relaxed mb-10">
              We've refined our approach over 19 years to deliver every project on time, on budget, and beyond expectations.
            </p>

            <div className="grid grid-cols-1 gap-5">
              {POINTS.map((point, i) => (
                <PointItem key={point.title} point={point} delay={i * 70} />
              ))}
            </div>

            <div className="mt-10">
              <a
                href="#consultation"
                onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-3 bg-forest-500 hover:bg-forest-600 text-white font-sans font-semibold text-sm px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                Get My Free Consultation
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PointItem({ point, delay }: { point: typeof POINTS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersectionObserver(ref, { threshold: 0.3 });

  return (
    <div
      ref={ref}
      className={`flex gap-4 transition-all duration-600 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-forest-50 rounded-lg mt-0.5">
        <i className={`${point.icon} text-forest-500 text-base`} />
      </div>
      <div>
        <h4 className="font-serif text-charcoal-900 text-base font-semibold mb-1">{point.title}</h4>
        <p className="font-sans text-charcoal-700 text-sm leading-relaxed">{point.desc}</p>
      </div>
    </div>
  );
}
